import React, { useState } from "react";

// Keep MUI Icons
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  MenuBook as MenuBookIcon,
  HelpOutline as HelpIcon,
  Description as FileIcon
} from "@mui/icons-material";
import NoAccess from "./NoAccess";

export default function GetHelp() {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(null);

  // --- ACCOUNTING SPECIFIC FAQS ---
  const faqs = [
    {
      question: "How do I create a new Journal Entry?",
      answer: "Navigate to the 'Accounts > Journals' section in the sidebar. Click on the 'New Entry' button. Ensure your total Debits equal your total Credits before posting to the General Ledger."
    },
    {
      question: "What is the difference between GL Accounts and Chart of Accounts?",
      answer: "The 'Chart of Accounts' is the complete index of all financial accounts available. 'GL Accounts' allows you to manage specific account details and their parent-child relationships (Sub Types)."
    },
    {
      question: "How do I manage Receivables and Payables?",
      answer: "Use the 'Receivables' menu to track money owed by customers and 'Payables' to track money you owe to suppliers. You can view outstanding balances and aging reports in these sections."
    },
    {
      question: "How do I set up a new Business Area or Company?",
      answer: "Go to the 'Maintenance' section in the sidebar. You can configure 'Business Area' and 'Company' settings there to properly segment your financial reporting."
    },
    {
      question: "How can I change the Document Type for a transaction?",
      answer: "Under 'Maintenance > Document Type', you can define various categories like Invoices, Receipts, or Credit Memos to keep your records organized."
    }
  ];

  // --- ACCOUNTING GLOSSARY ---
  const glossary = [
    { term: "Debit (DR)", def: "An entry that increases an asset or expense account, or decreases a liability or equity account." },
    { term: "Credit (CR)", def: "An entry that increases a liability, equity, or revenue account, or decreases an asset or expense account." },
    { term: "General Ledger", def: "The master record of all financial transactions within the organization, organized by account." },
    { term: "Chart of Accounts", def: "A structured list of every account used by the business to record financial transactions." },
    { term: "Accounts Payable", def: "Money owed by a business to its suppliers shown as a liability on the balance sheet." },
    { term: "Accounts Receivable", def: "Money owed to a business by its customers for goods or services delivered." },
  ];

  const filteredFaqs = faqs.filter((faq) =>
    faq.question.toLowerCase().includes(search.toLowerCase()) ||
    faq.answer.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  //#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasGetHelp) {
    return <NoAccess />;
  }
  //#endregion

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-800">
      
      {/* --- Header Section --- */}
      <div className="max-w-3xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
          Accounting Support Center
        </h1>
        <p className="text-gray-500 text-lg mb-8">
          Search for guides on Ledger management, Journals, and Financial Maintenance.
        </p>

        {/* Custom Search Bar */}
        <div className="relative max-w-xl mx-auto group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <SearchIcon className="text-gray-400 group-focus-within:text-theme-color transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Search help (e.g. 'ledger', 'journal', 'credits')..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-2xl shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-theme-color/20 focus:border-theme-color transition-all duration-200"
          />
        </div>
      </div>

      {/* --- Main Grid Layout --- */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN (FAQs & Glossary) */}
        <div className="lg:col-span-2 space-y-10">
          
          {/* FAQ Section */}
          <section>
            <div className="flex items-center gap-2 mb-6">
              <HelpIcon className="text-theme-color" />
              <h2 className="text-xl font-bold text-gray-900">System Usage FAQs</h2>
            </div>

            <div className="space-y-3">
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq, index) => (
                  <div 
                    key={index}
                    className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
                      activeIndex === index 
                        ? "border-theme-color shadow-md ring-1 ring-theme-color/10" 
                        : "border-gray-200 shadow-sm hover:border-theme-color/50"
                    }`}
                  >
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex justify-between items-center p-5 text-left focus:outline-none"
                    >
                      <span className={`font-semibold ${activeIndex === index ? "text-theme-color" : "text-gray-800"}`}>
                        {faq.question}
                      </span>
                      <ExpandMoreIcon 
                        className={`text-gray-400 transition-transform duration-300 ${
                          activeIndex === index ? "transform rotate-180 text-theme-color" : ""
                        }`} 
                      />
                    </button>
                    
                    <div 
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        activeIndex === index ? "max-h-60 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="p-5 pt-0 text-gray-600 leading-relaxed text-sm">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center bg-white border-2 border-dashed border-gray-200 rounded-xl">
                  <p className="text-gray-500">No matching help articles found for "{search}"</p>
                </div>
              )}
            </div>
          </section>

          {/* Glossary Section */}
          <section>
            <div className="flex items-center gap-2 mb-6">
              <MenuBookIcon className="text-theme-color" />
              <h2 className="text-xl font-bold text-gray-900">Accounting Terms Glossary</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {glossary.map((item, idx) => (
                <div 
                  key={idx} 
                  className="group bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-theme-color transition-all duration-200"
                >
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-theme-color transition-colors">
                    {item.term}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {item.def}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN (Sticky Sidebar) */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 sticky top-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6 border-b border-gray-100 pb-4">
              Financial Support
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-theme-color/10 text-theme-color">
                  <EmailIcon />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">System Admin</p>
                  <a href="mailto:finance-admin@yourdomain.com" className="text-theme-color hover:underline font-medium break-all">
                    finance-admin@yourdomain.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-theme-color/10 text-theme-color">
                  <PhoneIcon />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Internal Extension</p>
                  <p className="text-gray-800 font-semibold">Ext: 402 (Accounting Dept)</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-theme-color/10 text-theme-color">
                  <AccessTimeIcon />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Support Hours</p>
                  <p className="text-gray-800 text-sm">Mon - Fri, 9am - 6pm</p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button className="w-full mt-8 flex items-center justify-center gap-2 bg-theme-color hover:opacity-90 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-md active:scale-95">
              <FileIcon fontSize="small" />
              Download Chart of Accounts Guide
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}